var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Tabs, Tab, useMediaQuery, useTheme, Tooltip, } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { GridFilters } from "@ui/components/grid/GridFilters";
import { PrimaryGrid } from "@ui/components/grid/PrimaryGrid";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { TemperatureStatusBadge, getNodeTemperatureColor, getNodeTemperatureStatus, } from "@ui/components/temperature";
import StatusIndicator from "@ui/components/status/StatusIndicator";
import { SButton } from "@ui/components/buttons/SButton";
import { colCenter } from "@ui/components/style/style";
import { ConnectedIcon, DisconnectedIcon, } from "@ui/components/icons/ConnectionIcons";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { useTranslation } from "react-i18next";
import { GridCellStack } from "@ui/components/grid";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { getLatestApActivityIso } from "@utility/access-point-monitoring";
import { APDetailsCard } from "./components/APDetailsCard";
import { NodeActionsDialog } from "./components/NodeActionsDialog";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAccessPoint } from "@reducers/access-points/thunk/access-point-thunk";
import { getNodesByAccessPointUid, getAllNodes, forceConnectNode, forceDisconnectNode, replaceNodeV2, } from "@reducers/nodes/thunk/nodes-thunk";
import { toast } from "@ui/utils/toaster";
import { APDetailsCardSkeleton } from "./components/APDetailsCardSkeleton";
import { APDetailsNodeCard } from "./components/APDetailsNodeCard";
import localStorageAccessor from "@utility/local-storage-accessor";
import { NodeBehaviour } from "@reducers/nodes/models/nodes-dto";
import { useDebounce } from "@ui/utils/useDebounce";
import { cleanNodesFiltersForApi } from "./cleanNodesFiltersForApi";
import { Icon } from "@iconify/react";
import { FreeRoamingBadge } from "@ui/components/badges/FreeRoamingBadge";
import { alarmTypeLabel } from "@ui/utils/alarm-type-label";
export const AppDetails = () => {
    var _a, _b;
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { apId } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useAppDispatch();
    // Get AP data from Redux
    const { selectedAccessPoint, singleLoading, singleError } = useAppSelector((state) => state.accessPoint);
    // Get nodes data from Redux
    const { nodes, loading: nodesLoading, paginationInfo, } = useAppSelector((state) => state.nodes);
    // Local state - try to get cached AP first
    const [cachedAp, setCachedAp] = useState(() => localStorageAccessor.getSelectedAccessPoint());
    // Get the effective AP data (from Redux or cache)
    const effectiveAp = selectedAccessPoint || cachedAp;
    // Tab and filters state - declared before useEffect that uses it
    const [tabValue, setTabValue] = useState("connected");
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(25);
    const cleanFilters = useCallback(cleanNodesFiltersForApi, []);
    // Fetch nodes when filters, search, or tab changes (guard against StrictMode double-invoke)
    const nodesFetchKeyRef = useRef(null);
    // Always reflects the latest tabValue — readable inside async callbacks without stale closure
    const tabValueRef = useRef(tabValue);
    // Incrementing this forces the fetch effect to re-run (used to recover from stale responses)
    const [forceFetchTick, setForceFetchTick] = useState(0);
    useEffect(() => {
        tabValueRef.current = tabValue;
    }, [tabValue]);
    useEffect(() => {
        if (!apId)
            return;
        const apiFilters = cleanFilters(filters);
        // Add global search
        if (debouncedSearchTerm) {
            apiFilters.globalSearch = debouncedSearchTerm;
        }
        // Build a key from all dependencies to detect true changes vs StrictMode re-runs
        const fetchKey = JSON.stringify({
            apId,
            tabValue,
            filters,
            debouncedSearchTerm,
            currentPage,
            currentPageSize,
        });
        if (nodesFetchKeyRef.current === fetchKey)
            return;
        nodesFetchKeyRef.current = fetchKey;
        const capturedTab = tabValue;
        // After a fetch completes, check whether the user has already switched tabs.
        // If so, the Redux `nodes` state now contains stale data for the wrong tab.
        // Reset the key guard and bump forceFetchTick so this effect re-fires and
        // overwrites Redux with the correct tab's data.
        const afterFetch = () => {
            if (tabValueRef.current !== capturedTab) {
                nodesFetchKeyRef.current = null;
                setForceFetchTick((t) => t + 1);
            }
        };
        if (tabValue === "disconnected") {
            // Disconnected nodes (all APs): server-side pagination
            dispatch(getAllNodes({
                pageNumber: currentPage,
                pageSize: currentPageSize,
                filters: Object.assign(Object.assign({}, apiFilters), { simulatedDisconnectedOnly: true }),
            })).then(afterFetch).catch(() => { });
        }
        else {
            // Fetch nodes for this specific AP
            dispatch(getNodesByAccessPointUid({
                accessPointUid: apId,
                query: {
                    pageNumber: currentPage,
                    pageSize: currentPageSize,
                    filters: apiFilters,
                },
            })).then(afterFetch).catch(() => { });
        }
    }, [
        dispatch,
        apId,
        tabValue,
        filters,
        debouncedSearchTerm,
        cleanFilters,
        currentPage,
        currentPageSize,
        forceFetchTick,
    ]);
    // Fetch AP data on mount (guard against StrictMode double-invoke)
    const apFetchedRef = useRef(null);
    useEffect(() => {
        if (apId && apFetchedRef.current !== apId) {
            apFetchedRef.current = apId;
            dispatch(getAccessPoint(apId));
        }
    }, [dispatch, apId]);
    // Cache AP data when it's loaded from Redux
    useEffect(() => {
        if (selectedAccessPoint) {
            localStorageAccessor.setSelectedAccessPoint(selectedAccessPoint);
            setCachedAp(selectedAccessPoint);
        }
    }, [selectedAccessPoint]);
    // Handle search change
    const handleSearchChange = useCallback((event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    }, []);
    // Handle filter change
    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
    }, []);
    // State for dialogs
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    // Transform API nodes data to grid format
    const apNodes = React.useMemo(() => {
        if (!nodes)
            return [];
        return nodes.map((node) => {
            var _a, _b, _c, _d, _e;
            return ({
                id: node.id,
                temperature: (_a = node.nodeTemprature) !== null && _a !== void 0 ? _a : null,
                nodeTemperature: (_b = node.nodeTemprature) !== null && _b !== void 0 ? _b : null,
                lowTemperature: node.lowTemperature,
                highTemperature: node.highTemperature,
                nodeTypeCode: node.nodeTypeCode,
                leftIndicator: node.nodeTypeCode === 1 ? 1 : undefined,
                rightIndicator: node.nodeTypeCode === 1 ? 2 : undefined,
                nodeName: node.nodeName,
                nodeId: node.nodeUid,
                isActive: node.isActive,
                status: node.isActive && node.nodeBehaviour == NodeBehaviour.Normal
                    ? "Active"
                    : "Inactive",
                deviceName: node.accessPointName || "",
                deviceId: node.accessPointUid || "",
                lastUpdate: node.lastUpdated,
                nodeBehaviour: (_c = node.nodeBehaviour) !== null && _c !== void 0 ? _c : NodeBehaviour.Normal,
                lastAlarm: (_d = node.lastAlarm) !== null && _d !== void 0 ? _d : null,
                freeRoamingNode: (_e = node.freeRoamingNode) !== null && _e !== void 0 ? _e : false,
            });
        });
    }, [nodes]);
    const lastAlarmTooltipContent = useCallback((a) => (React.createElement(Box, { sx: { py: 0.5, maxWidth: 260 } },
        React.createElement(Typography, { variant: "subtitle2", sx: { mb: 0.75, fontWeight: 600 } }, t("common:grid.lastAlarm")),
        React.createElement(Typography, { variant: "body2", sx: { display: "block", mb: 0.5 } },
            t("common:grid.alarmType"),
            ": ",
            alarmTypeLabel(a.alarmType, t)),
        React.createElement(Typography, { variant: "body2", sx: { display: "block", mb: 0.5 } },
            t("common:grid.temperatureAtAlarm"),
            ":",
            " ",
            a.temperature != null && !Number.isNaN(Number(a.temperature))
                ? `${Number(a.temperature).toFixed(1)}°`
                : t("common:grid.noTemperatureForAlarm")),
        React.createElement(Typography, { variant: "body2", sx: { display: "block", mb: 0.5 } },
            t("common:grid.alarmTime"),
            ":",
            " ",
            formatUtcToLocalDate(a.alarmStartDateTime),
            " ",
            formatUtcToLocalTime(a.alarmStartDateTime)),
        React.createElement(Typography, { variant: "body2", sx: { display: "block" } },
            t("common:grid.acknowledgementStatus"),
            ":",
            " ",
            a.isAlarmAcknowledged
                ? t("common:grid.acknowledged")
                : t("common:grid.unacknowledged")))), [t]);
    const columns = [
        {
            field: "node",
            headerName: t("grid.ap"),
            minWidth: 100,
            filterable: false,
            type: "string",
            renderCell: (params) => (React.createElement(TemperatureDisplay, { width: 91, temperature: params.row.nodeTemperature, color: params.row.nodeTypeCode === 1 ? "#1D95E2" : getNodeTemperatureColor(params.row), leftIndicator: params.row.leftIndicator, rightIndicator: params.row.rightIndicator })),
        },
        {
            field: "nodeName",
            headerName: t("grid.nameId"),
            flex: 1.5,
            minWidth: 180,
            filterable: true,
            filterType: "text",
            type: "string",
            renderCell: (params) => {
                const alarm = params.row.lastAlarm;
                const showAlarmMarker = tabValue === "connected" &&
                    alarm != null &&
                    params.row.nodeTypeCode !== 1;
                return (
                // width: 100% is what lets the name box below actually claim the leftover
                // width, so the badge lands at the same x position on every row.
                React.createElement(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        height: "100%",
                        width: "100%",
                        minWidth: 0,
                    } },
                    React.createElement(Box, { sx: { minWidth: 0, flex: 1 } },
                        React.createElement(GridCellStack, { topText: params.row.nodeName, bottomText: params.row.nodeId })),
                    params.row.freeRoamingNode && React.createElement(FreeRoamingBadge, null),
                    showAlarmMarker && (React.createElement(Tooltip, { title: lastAlarmTooltipContent(alarm), arrow: true, placement: "top", enterTouchDelay: 0 },
                        React.createElement(Box, { component: "span", sx: {
                                display: "inline-flex",
                                alignItems: "center",
                                flexShrink: 0,
                                cursor: "default",
                                color: alarm.isAlarmAcknowledged
                                    ? "text.secondary"
                                    : "error.main",
                            }, "aria-label": t("common:grid.lastAlarm") },
                            React.createElement(Icon, { icon: alarm.isAlarmAcknowledged
                                    ? "solar:bell-bold"
                                    : "solar:danger-triangle-bold", width: 20, height: 20 }))))));
            },
        },
        {
            field: "temperature",
            headerName: t("grid.temperature"),
            minWidth: 100,
            filterable: false,
            type: "string",
            renderCell: (params) => (React.createElement(Box, { sx: { height: "100%", display: "flex", alignItems: "center" } },
                React.createElement(TemperatureStatusBadge, { status: params.row.nodeTypeCode === 1 ? "normal" : getNodeTemperatureStatus(params.row) }))),
        },
        {
            field: "status",
            headerName: t("grid.status"),
            flex: 1,
            minWidth: 115,
            filterable: true,
            filterType: "select",
            filterOptions: ["Active", "Inactive"],
            type: "string",
            renderCell: (params) => (React.createElement(StatusIndicator, { variant: params.row.status === "Active" ? "active" : "inactive" })),
        },
        {
            field: "lastUpdate",
            headerName: t("grid.lastUpdate"),
            flex: 1.2,
            minWidth: 120,
            filterable: false,
            type: "string",
            renderCell: (params) => {
                const isNoCommunication = params.row.nodeBehaviour === NodeBehaviour.NoNodeCommunication;
                const errorColor = theme.palette.error.main;
                return (React.createElement(GridCellStack, Object.assign({ topText: formatUtcToLocalDate(params.row.lastUpdate), bottomText: formatUtcToLocalTime(params.row.lastUpdate) }, (isNoCommunication && {
                    topStyle: { color: errorColor },
                    bottomStyle: { color: errorColor },
                }))));
            },
        },
        {
            field: "actions",
            headerName: t("grid.actions"),
            minWidth: 300,
            filterable: false,
            type: "string",
            renderCell: (params) => {
                return (React.createElement(Box, { sx: { display: "flex", gap: 1 } },
                    PermissionChecker.hasPermission(Permission.ACCESSPOINTS_DELETE) &&
                        (tabValue === "disconnected" ? (
                        // Disconnected tab: Show Connect button
                        React.createElement(SButton, { variant: "soft", color: "success", onClick: (e) => {
                                e.stopPropagation();
                                setSelectedNode(params.row);
                                setIsDialogOpen(true);
                            } }, t("actions.couple"))) : (
                        // Connected tab: Show Disconnect button for all nodes
                        React.createElement(SButton, { variant: "soft", color: "error", onClick: (e) => {
                                e.stopPropagation();
                                setSelectedNode(params.row);
                                setIsDialogOpen(true);
                            } }, t("actions.disconnect")))),
                    React.createElement(SButton, { variant: "soft", color: "primary", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/nodes/${params.row.nodeId}/logs/${apId}`);
                        } }, t("actions.logs")),
                    PermissionChecker.hasPermission(Permission.NODES_EDIT) && (React.createElement(SButton, { variant: "outlined", color: "primary", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/nodes/${params.row.id}/edit`);
                        } }, t("actions.settings")))));
            },
        },
    ];
    // Server returns the correct page; disconnected list uses simulatedDisconnectedOnly on the API
    const filteredNodes = React.useMemo(() => {
        if (!apId)
            return [];
        return apNodes;
    }, [apNodes, apId]);
    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedNode(null);
    };
    // Determine if current action is connect or disconnect based on tab
    const isConnectAction = selectedNode && tabValue === "disconnected";
    const handleNodeAction = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNode || !apId)
            return;
        try {
            if (isConnectAction) {
                // Handle force connect
                yield dispatch(forceConnectNode({
                    nodeId: selectedNode.id,
                    accessPointUid: apId,
                })).unwrap();
                // Small delay to ensure database update is complete, then refresh disconnected list
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    const refreshFilters = cleanFilters(filters);
                    if (debouncedSearchTerm) {
                        refreshFilters.globalSearch = debouncedSearchTerm;
                    }
                    nodesFetchKeyRef.current = null;
                    yield dispatch(getAllNodes({
                        pageNumber: currentPage,
                        pageSize: currentPageSize,
                        filters: Object.assign(Object.assign({}, refreshFilters), { simulatedDisconnectedOnly: true }),
                    })).unwrap();
                }), 200);
            }
            else {
                // Handle force disconnect
                yield dispatch(forceDisconnectNode({
                    nodeId: selectedNode.id,
                })).unwrap();
                // Small delay to ensure database update is complete, then refresh
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    if (tabValue === "disconnected") {
                        const refreshFilters = cleanFilters(filters);
                        if (debouncedSearchTerm) {
                            refreshFilters.globalSearch = debouncedSearchTerm;
                        }
                        yield dispatch(getAllNodes({
                            pageNumber: currentPage,
                            pageSize: currentPageSize,
                            filters: Object.assign(Object.assign({}, refreshFilters), { simulatedDisconnectedOnly: true }),
                        })).unwrap();
                    }
                    else {
                        // Refresh nodes for this AP
                        yield dispatch(getNodesByAccessPointUid({
                            accessPointUid: apId,
                            query: {
                                pageNumber: 1,
                                pageSize: 25,
                                filters: {},
                            },
                        })).unwrap();
                    }
                }), 200);
            }
            handleDialogClose();
        }
        catch (error) {
            console.error(`Failed to ${isConnectAction ? "force connect" : "force disconnect"} node:`, error);
        }
    });
    const handleReplaceNode = (replacementNodeId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNode || !apId)
            return;
        try {
            yield dispatch(replaceNodeV2({
                replacedNodeId: selectedNode.id,
                replacementNodeId,
                accessPointUid: apId,
            })).unwrap();
            toast.success(t("toasts.nodeReplacedSuccessfully"));
            handleDialogClose();
            const apiFilters = cleanFilters(filters);
            if (debouncedSearchTerm) {
                apiFilters.globalSearch = debouncedSearchTerm;
            }
            yield dispatch(getNodesByAccessPointUid({
                accessPointUid: apId,
                query: {
                    pageNumber: currentPage,
                    pageSize: currentPageSize,
                    filters: apiFilters,
                },
            })).unwrap();
        }
        catch (error) {
            const msg = typeof error === "string"
                ? error
                : error === null || error === void 0 ? void 0 : error.message;
            toast.error(msg || t("toasts.nodeReplaceFailed"));
        }
    });
    const showNodeReplaceTab = !isConnectAction && PermissionChecker.hasPermission(Permission.NODES_EDIT);
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t(""), showBackButton: true }),
        React.createElement(PageContent, { sx: { pb: 1 } },
            singleLoading && !effectiveAp ? (React.createElement(APDetailsCardSkeleton, null)) : (React.createElement(APDetailsCard, { name: (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointName) || t("actions.unknownAp"), deviceId: (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointUID) || apId || "", apId: apId, status: (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.isActive) ? "active" : "inactive", lastUpdatedAt: effectiveAp ? getLatestApActivityIso(effectiveAp) : undefined, address: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.address, place: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.place, calibrationDate: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.calibrationDate, onSettingsClick: PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT)
                    ? () => navigate(`/dashboard/${apId}/edit`)
                    : undefined })),
            React.createElement(GridFilters, { title: `${(_a = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _a !== void 0 ? _a : filteredNodes.length} ${t("pages.nodes")}`, loading: nodesLoading, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange, rightSection: React.createElement(Tabs, { value: tabValue, onChange: (_, v) => {
                        if (isDialogOpen)
                            return;
                        setTabValue(v);
                        setCurrentPage(1);
                    }, sx: { minHeight: 40 } },
                    React.createElement(Tab, { value: "connected", label: React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                            React.createElement(ConnectedIcon, { width: 20, height: 20 }),
                            React.createElement("span", null, t("status.connected"))) }),
                    React.createElement(Tab, { value: "disconnected", label: React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                            React.createElement(DisconnectedIcon, { width: 20, height: 20 }),
                            React.createElement("span", null, t("status.disconnected"))) })) }),
            React.createElement(PrimaryGrid, { rows: nodesLoading ? [] : filteredNodes, columns: columns, loading: nodesLoading, heightVariant: "dashboard", checkboxSelection: false, autoHeight: true, paginationMode: "server", renderCard: (row) => (React.createElement(APDetailsNodeCard, { row: row, tabValue: tabValue, onAction: (node) => {
                        setSelectedNode(node);
                        setIsDialogOpen(true);
                    }, onLogs: (nodeId) => navigate(`/nodes/${nodeId}/logs/${apId}`), onSettings: (id) => navigate(`/nodes/${id}/edit`) })), rowCount: (_b = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _b !== void 0 ? _b : 0, paginationModel: {
                    page: currentPage - 1,
                    pageSize: currentPageSize,
                }, onPaginationModelChange: (model) => {
                    if (model.pageSize !== currentPageSize) {
                        setCurrentPageSize(model.pageSize);
                        setCurrentPage(1);
                    }
                    else if (model.page + 1 !== currentPage) {
                        setCurrentPage(model.page + 1);
                    }
                }, pageSizeOptions: [25, 50] })),
        React.createElement(NodeActionsDialog, { isOpen: isDialogOpen, onClose: handleDialogClose, onConfirm: handleNodeAction, selectedNode: selectedNode, action: isConnectAction ? "connect" : "disconnect", accessPointName: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointName, showReplaceTab: showNodeReplaceTab, onReplace: showNodeReplaceTab ? handleReplaceNode : undefined })));
};
